import createAuth0Client from '@auth0/auth0-spa-js';

import { isAuth0InitResponse, Auth0InitResponse, Auth0ClientOptionsInit } from './auth0Types';
import Auth0Identity from './identity';
import { transformAuth0Claims } from '../utils/transform';

/**
 * Initializes auth0 client
 *
 * @param options configuration options accepted by createAuth0Client
 *
 *
 * @returns
 * A __Promise__ with methods and properties provided by the Auth0 client. When successful, `auth0` will be populated with this data.
 *
 * <details>
 * <summary>
 * On success (resolves)
 * </summary>
 *
 * ```json
 * {
 *  cache": {
 *    "save": () => {},
 *    "get": () => {},
 *    "clear": () => {}
 *  },
 *  "cacheLocation":"memory",
 *  "cookieStorage":{
 *    "save": () => {},
 *    "get": () => {},
 *    "remove": () => {}
 *  },
 *  "customOptions": {
 *    "prompt":"consent"
 *  },
 *  "defaultScope":"openid profile email",
 *  "domainUrl":"https://dev-dersptm1.auth0.com",
 *  "options":{
 *    "domain":"dev-dersptm1.auth0.com",
 *    "client_id":"VuTdg8C48TQ15ni148QzadSG9eEplYxi",
 *    "redirect_uri":"http://127.0.0.1:8080",
 *    "prompt":"consent",
 *    "useRefreshTokens":true
 *  },
 *  "scope":"offline_access",
 *  "sessionCheckExpiryDays":1,
 *  "tokenIssuer":"https://dev-dersptm1.auth0.com/",
 *  "transactionManager":{
 *    "storage":{
 *      "save": () => {},
 *      "get": () => {},
 *      "remove": () => {}
 *    },
 *    "transaction":null
 *  },
 *  "worker":{
 *    "onmessage":null,
 *    "onerror":null
 *  }
 * }
 * ```
 * </details>
 *
 * <details>
 * <summary>
 * On error (throws)
 * </summary>
 *
 * ```
 * See documentation for corresponding error codes:
 * https://auth0.github.io/auth0-spa-js/index.html
 * ```
 * </details>
 */

export default async function initAuth0(
  options: Auth0ClientOptionsInit
): Promise<Auth0InitResponse> {
  return await createAuth0Client(options).then(async resp => {
    if (isAuth0InitResponse(resp)) {
      Auth0Identity.auth0 = resp;
      const tokenInfo = await (Auth0Identity.auth0 || resp).getIdTokenClaims();
      const transformedAuth0Claims = transformAuth0Claims(tokenInfo);
      Auth0Identity.userIdentity = transformedAuth0Claims;
      return Auth0Identity.auth0;
    } else {
      throw resp;
    }
  });
}
